import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, Clock, ChevronDown, Navigation } from "lucide-react";
import { AssignBadge, Empty } from "@/components/atlas/Shared";
import { EventInfo } from "@/components/atlas/EventInfo";
import { DocumentList } from "@/components/documents/DocumentList";
import { ClockPanel } from "@/components/time/ClockPanel";
import { ShiftCalendar, ViewSwitch } from "@/components/time/ShiftCalendar";
import { AddToCalendar } from "@/components/time/AddToCalendar";
import { AvailabilityDialog, useAvailability } from "@/components/availability/EventCard";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useFetch, usePositions } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";
import { fmtDate, fmtRange, fmtTime } from "@/lib/format";

const Item = ({ a, onRespond, onClock, i, focused }) => {
  const { t, lang } = useLang();
  const { label } = usePositions();
  const [open, setOpen] = useState(false);
  const ev = a.event;
  const cancelled = a.status === "cancelled" || ev.status === "cancelled";
  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.address || ev.location)}`;
  return (
    <article className={`card-lux rise scroll-mt-28 ${cancelled ? "opacity-60" : ""} ${focused ? "ring-2 ring-[#C5A059]" : ""}`} style={{ animationDelay: `${i * 60}ms`, transition: "box-shadow 0.3s" }} data-testid={`schedule-item-${a.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="eyebrow capitalize">{fmtDate(ev.date, lang)}</div>
          <AssignBadge status={ev.status === "cancelled" ? "cancelled" : a.status} testid={`schedule-status-${a.id}`} />
        </div>
        <h3 className="font-display text-2xl font-semibold mt-2 leading-tight">{ev.name}</h3>
        <div className="mt-1 text-sm font-medium text-[#8a6d2f]" data-testid={`schedule-position-${a.id}`}>{label(a.slot.position_key)}</div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-neutral-600">
          <div className="flex items-start gap-1.5"><Clock className="w-3.5 h-3.5 text-[#C5A059] mt-0.5" /><div><div className="mono">{fmtRange(a.slot.start_time || ev.start_time, a.slot.end_time || ev.end_time, t)}</div>{ev.report_time && <div className="text-neutral-400">{t("c.reporting")} {fmtTime(ev.report_time, t)}</div>}</div></div>
          <a href={maps} target="_blank" rel="noreferrer" className="flex items-start gap-1.5 hover:text-black"><MapPin className="w-3.5 h-3.5 text-[#C5A059] mt-0.5" /><span>{ev.location}{ev.address && <span className="block text-neutral-400">{ev.address}</span>}</span></a>
        </div>
        {a.status === "pending" && !cancelled && !a.timesheet && (
          <div className="mt-5">
            <p className="text-sm mb-2">{t("p.confirmQ")}</p>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid={`confirm-assignment-${a.id}`} onClick={() => onRespond(a, "confirm")} className="press h-12 rounded-full bg-[#C5A059] text-[#0A0A0C] text-sm font-semibold hover:bg-[#D9B872]">{t("c.confirm")}</button>
              <button data-testid={`decline-assignment-${a.id}`} onClick={() => onRespond(a, "decline")} className="press h-12 rounded-full border border-black/15 text-sm hover:border-red-400 hover:text-red-600">{t("c.decline")}</button>
            </div>
          </div>
        )}
        <ClockPanel a={a} onChange={onClock} />
        {!cancelled && a.status !== "declined" && <AddToCalendar a={a} />}
        <button data-testid={`toggle-details-${a.id}`} onClick={() => setOpen(!open)} className="press mt-4 w-full flex items-center justify-between text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-black pt-3 border-t border-black/[0.06]">
          {t("p.details")}<ChevronDown className={`w-4 h-4 ${open ? "rotate-180" : ""}`} style={{ transition: "transform 0.2s" }} />
        </button>
        {open && (
          <div className="mt-2" data-testid={`schedule-details-${a.id}`}>
            {a.slot.instructions && <p className="text-sm py-3 border-b border-black/[0.06]">{a.slot.instructions}</p>}
            <EventInfo event={ev} />
            <DocumentList eventId={ev.id} />
            <a href={maps} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-3 text-xs text-[#8a6d2f]"><Navigation className="w-3.5 h-3.5" />Google Maps</a>
          </div>
        )}
      </div>
    </article>
  );
};

export default function Schedule() {
  const { t } = useLang();
  const { user } = useAuth();
  const { data, setData, reload } = useFetch("/me/schedule");
  const avail = useAvailability(user.role === "employee" || !!user.is_staff);
  const [openEvent, setOpenEvent] = useState(null);
  const [view, setView] = useState("list");
  const [focus, setFocus] = useState(null);
  useEffect(() => {
    if (view !== "list" || !focus) {
      return;
    }
    document.querySelector(`[data-testid="schedule-item-${focus}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    const timer = setTimeout(() => setFocus(null), 2500);
    return () => clearTimeout(timer);
  }, [view, focus]);
  const openShift = (en) => {
    if (en.kind === "event") {
      setOpenEvent(en.x.id);
      return;
    }
    setFocus(en.x.id);
    setView("list");
  };
  const respond = async (a, action) => {
    try {
      const r = await api.post(`/me/assignments/${a.id}/respond`, { action });
      setData(data.map((x) => (x.id === a.id ? { ...x, status: r.data.status } : x)));
      reload();
      toast.success(action === "confirm" ? t("p.confirmed") : t("p.declined"));
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };
  return (
    <div>
      <div className="mb-8 rise">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="page-title">{t("p.scheduleTitle")}</h1>
        <p className="text-sm text-neutral-500 mt-3">{t("p.scheduleSub")}</p>
        <div className="mt-5"><ViewSwitch view={view} setView={setView} /></div>
      </div>
      {data && data.length === 0 && <Empty text={t("p.noSchedule")} testid="schedule-empty" />}
      {view === "calendar" ? <ShiftCalendar items={data} events={avail.data} onOpen={openShift} /> : (
        <div className="space-y-5" data-testid="schedule-list">
          {(data || []).map((a, i) => <Item key={a.id} a={a} i={i} focused={focus === a.id} onRespond={respond} onClock={reload} />)}
        </div>
      )}
      <AvailabilityDialog event={(avail.data || []).find((e) => e.id === openEvent)} onClose={() => setOpenEvent(null)} onChange={avail.onChange} />
    </div>
  );
}
